import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../api';
import s from './TenantSwitchModal.module.css';

/**
 * "Byt företag" modal — KanPlan design-system Del C modal structure.
 * Standard light header (no dark gradient), full dark-mode support via CSS module.
 */

const STORAGE_KEY = 'kanplan-active-tenant-slug';
const STORAGE_ROLE_KEY = 'kanplan-active-tenant-role';
const STORAGE_NAME_KEY = 'kanplan-active-tenant-name';

export default function TenantSwitchModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  const activeSlug = useMemo(() => {
    try { return (localStorage.getItem(STORAGE_KEY) || '').trim(); } catch { return ''; }
  }, [open]); // re-read on each open

  // Fetch tenants when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSearch('');
    (async () => {
      try {
        const res = await fetch(`${API_URL}/tenants/mine`, { credentials: 'include' });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
          throw new Error(msg);
        }
        const data = await res.json();
        const arr = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) setItems(arr);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Kunde inte hämta företag');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Auto-focus search when opened
  useEffect(() => {
    if (open && !loading && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, loading]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return items;
    return items.filter(t => {
      const hay = `${t?.name || ''} ${t?.slug || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  function selectTenant(t) {
    try {
      localStorage.setItem(STORAGE_KEY, String(t.slug || '').trim());
      if (t.role) localStorage.setItem(STORAGE_ROLE_KEY, String(t.role || '').trim());
      localStorage.setItem(STORAGE_NAME_KEY, String(t.legal_name || t.name || '').trim());
    } catch {}
    try { window.location.reload(); } catch {}
  }

  if (!open) return null;

  return (
    <div className={s.overlay} onClick={onClose}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>

        {/* ── HEADER (Del C standard) ── */}
        <div className={s.head}>
          <div className={s.headLeft}>
            <span className={s.headIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18"/>
                <path d="M5 21V7l8-4v18"/>
                <path d="M19 21V11l-6-4"/>
                <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/>
              </svg>
            </span>
            <div>
              <div className={s.headTitle}>Byt företag</div>
              <div className={s.headSubtitle}>Välj det företag du vill arbeta i</div>
            </div>
          </div>
          <button type="button" className={s.closeBtn} onClick={onClose} title="Stäng">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* ── BODY ── */}
        <div className={s.body}>
          {/* Search */}
          <div className={s.searchWrap}>
            <svg className={s.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              className={s.searchInput}
              placeholder="Sök företag..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loading && <div className={s.loading}><div className="kp-skel" style={{ width: '60%', height: 14, margin: '0 auto 10px', borderRadius: 6 }} /><div className="kp-skel" style={{ width: '40%', height: 14, margin: '0 auto', borderRadius: 6 }} /></div>}

          {error && <div className={s.error}>{error}</div>}

          {!loading && !error && (
            <div className={s.list}>
              {filtered.map(t => {
                const isCurrent = String(t?.slug) === String(activeSlug);
                return (
                  <button
                    key={t.id || t.slug}
                    type="button"
                    className={isCurrent ? s.tenantBtnActive : s.tenantBtn}
                    onClick={() => {
                      if (isCurrent) { onClose(); return; }
                      selectTenant(t);
                    }}
                  >
                    <span className={isCurrent ? s.avatarActive : s.avatar}>
                      {String(t.name || t.slug || '?').charAt(0).toUpperCase()}
                    </span>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={s.tenantName}>{t.name || t.slug}</div>
                      <div className={s.tenantMeta}>
                        {t.slug}{t.role ? ` · ${t.role}` : ''}
                      </div>
                    </div>

                    {isCurrent && <span className={s.activeBadge}>Aktiv</span>}
                  </button>
                );
              })}

              {!filtered.length && !loading && (
                <div className={s.empty}>Inga företag matchar sökningen.</div>
              )}
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className={s.foot}>
          <button type="button" className={s.footBtn} onClick={onClose}>
            Stäng
          </button>
        </div>
      </div>
    </div>
  );
}
